import React, { createContext, useContext, useState, useEffect } from "react";
import { firestore, storage } from "../backend/firebaseconfig";
import { getDocs, collection, query, where } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { useUserEmail } from "./UserEmailContext";
import { useUserRole } from "./UserRoleContext";
import { useAdminAdviserMails } from "./AdminAdviserEmailsContext";

const BrandingContext = createContext();

const DEFAULT_TRADING_NAME = "Rosemount Financial Solutions (IFA) Ltd";
const DEFAULT_PRIMARY_COLOR = "081e56";
const DEFAULT_REG_TEXT =
  "Rosemount Financial Solutions (IFA) Ltd is authorised and regulated by the Financial Conduct Authority (FCA). Entered on the FCA Register (https://register.fca.org.uk/) under reference 535515. Rosemount Financial Solutions (IFA) Ltd is registered in England and Wales, Registered No. 05668905, Registered Address Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU.";

export const BrandingProvider = ({ children }) => {
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { adminAdviserMails, currentAdviserEmail } = useAdminAdviserMails();

  const [tradingName, setTradingName] = useState(DEFAULT_TRADING_NAME);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [logo, setLogo] = useState("");
  const [regulatoryText, setRegulatoryText] = useState(DEFAULT_REG_TEXT);

  useEffect(() => {
    const resetBranding = () => {
      setTradingName(DEFAULT_TRADING_NAME);
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      setRegulatoryText(DEFAULT_REG_TEXT);
      setLogo("");
    };

    const fetchBranding = async () => {
      try {
        let targetEmail = userEmail;
        let lookupRole = userRole;

        if (
          (userRole === "Administrators" || userRole === "External") &&
          (currentAdviserEmail || adminAdviserMails?.length > 0)
        ) {
          targetEmail = currentAdviserEmail || adminAdviserMails[0];
          lookupRole = "Advisers";
        }

        if (!targetEmail || !lookupRole) {
          resetBranding();
          return;
        }

        const normalizedEmail = String(targetEmail || "")
          .toLowerCase()
          .trim();

        const colRef = collection(
          firestore,
          `Authorized Members/${lookupRole}/Data`,
        );

        const q = query(colRef, where("email", "==", normalizedEmail));
        const snap = await getDocs(q);

        if (snap.empty) {
          resetBranding();
          return;
        }

        const data = snap.docs[0].data() || {};

        const name = data.brandName || DEFAULT_TRADING_NAME;
        const color = data.primaryColor || DEFAULT_PRIMARY_COLOR;
        const regText = data.regulatoryText || DEFAULT_REG_TEXT;

        setTradingName(name);
        setPrimaryColor(color);
        setRegulatoryText(regText);

        // console.log("Branding loaded for adviser:", normalizedEmail);
        // console.log("Trading Name:", name);
        // console.log("Primary Color:", color);
        // console.log("Regulatory Text:", regText);

        try {
          const jpgRef = ref(storage, `Logos/${name}.jpg`);
          const jpgUrl = await getDownloadURL(jpgRef);
          setLogo(jpgUrl);
        } catch {
          try {
            const pngRef = ref(storage, `Logos/${name}.png`);
            const pngUrl = await getDownloadURL(pngRef);
            setLogo(pngUrl);
          } catch {
            setLogo("");
          }
        }
      } catch {
        resetBranding();
      }
    };

    fetchBranding();
  }, [userEmail, userRole, adminAdviserMails, currentAdviserEmail]);

  return (
    <BrandingContext.Provider
      value={{ tradingName, primaryColor, logo, regulatoryText }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
